import { useForm } from "react-hook-form";
import { FormButtons } from "../common/forms/FormButtons";
import { FormHeader } from "../common/forms/FormHeader";
import styles from "../common/forms/forms.module.css";
import { CustomTypedEntityFormProps } from "../common/entity/EntityInterfaces";
import { InventoryItem, inventoryItemsConstants } from "../api/Inventories";
import {
  EntityCustomTypeForm,
  useCustomTypeForm,
  useCustomTypeResolver,
} from "../Customization/CustomTypes/generics/useCustomTypeForm";
import { useCallback, useContext } from "react";
import { InputFormField } from "../common/formfields/InputFormField";
import * as yup from "yup";
import { ProjectsVirtualizedSelectForm } from "../common/forms/EntityForms/formsVirtualized/ProjectsVirtualizedSelectForm";
import { calculateDirtyFields } from "../common/forms/utils/FormUtils";
import { SessionContext } from "../common/contexts/SessionContext";

export const inventoryItemFormBaseSchema = {
  name: yup.string().required("Inventory name is required").typeError("Inventory name is required"),
  description: yup.string().optional(),
  customType: yup.object().required("Custom type is required").typeError("Custom type is required"),
};

export const InventoryItemForm = ({
  fieldLabels,
  permissions,
  title,
  subtitle,
  initialValues,
  onSubmit,
  onCancel,
  loading,
  submitButtonLabel,
  event,
  typesOverride,
  typeId,
}: CustomTypedEntityFormProps<"inventoryItems">) => {
  const { session } = useContext(SessionContext);

  const { typedResolver } = useCustomTypeResolver<Partial<InventoryItem>>(inventoryItemFormBaseSchema);

  // const { defaultValues, typedFormSchema, type, processCustomFields, setType, types } = useCustomTypeForm({
  //   initialValues: initialValues,
  //   formSchema: inventoryItemFormBaseSchema,
  //   typeId: initialValues?.customType?.id,
  //   entityType: "Inventory",
  // });

  const {
    watch,
    reset,
    getValues,
    register,
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<Partial<InventoryItem>>({
    defaultValues: initialValues,
    resolver: typedResolver,
  });

  const { processCustomFields, types } = useCustomTypeForm({
    control,
    reset,
    getValues,
    entityType: "InventoryItem",
    typeId: initialValues?.customType?.id,
    typesOverride,
  });

  // Helper function to process some fields
  const processEntity = useCallback((entity: Partial<InventoryItem>) => {
    return {
      ...entity,
      customType: entity.customType ?? null,
      ...(!entity.customType && { customFields: {} }),
    } as Partial<InventoryItem>;
  }, []);

  const customType = watch("customType");
  const type = customType ? types?.[customType.id] : undefined;

  return (
    <>
      <FormHeader title={title} subtitle={subtitle || initialValues?.customType?.name} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
        autoComplete="off"
        className={`form-horizontal ${styles.form_holder}`}
      >
        <fieldset>
          <legend className="col-md-offset-2 col-md-10">Details</legend>
        </fieldset>

        <InputFormField
          id="name"
          label={fieldLabels.name}
          errors={errors}
          register={register}
          autoFocus={true}
          required={true}
        />

        <ProjectsVirtualizedSelectForm
          id="projects"
          label={fieldLabels.projects}
          control={control}
          isMulti
          horizontal
          allowCreateEntity
          showControls
          disabled={!!type?.rootHierarchy}
          filters={{ currentUserHasAddPermission: true }}
        />

        {/* Render custom type */}
        <EntityCustomTypeForm
          entityType="InventoryItem"
          types={types}
          control={control}
          setValue={setValue}
          initialValues={initialValues}
          errors={errors}
          required
          disableTypeSelection={!!initialValues?.customType && (event === "ADD" || event === "CLONE")}
          disableParentSelection={!!initialValues?.parent && (event === "ADD" || event === "CLONE")}
          entityConstants={inventoryItemsConstants}
          customTypeFilters={{
            hasRestrictedAddPermission: !session?.permissions.add_admin_inventory_items ? false : undefined,
          }}
        />

        <FormButtons
          onClose={onCancel}
          onSubmit={handleSubmit(async (entity) => {
            await trigger();
            await onSubmit(processCustomFields(processEntity(entity)));
          })}
          submitButtonLabel={submitButtonLabel || "Save changes"}
          disabled={isSubmitting}
          errors={errors}
          loading={loading}
          reset={["EDIT", "CLONE"].includes(event) ? () => reset(initialValues) : undefined}
          dirtyFields={["EDIT", "CLONE"].includes(event) ? calculateDirtyFields(dirtyFields) : undefined}
        />
      </form>
    </>
  );
};
